import { useTranslation } from 'react-i18next';

import { ReactComponent as Check } from 'assets/icons/Check.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

const useDeleteWorkspaceRole = () => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const { matrixClient } = useMatrixContext();
  const { workspaceId, workspaceRoles, roleAssignments } = useWorkspacesContext({ requireWorkspaceId: true });

  // AC-094b: delete is blocked when the default Member role is not present in roles[].
  // Wave-4 T-017 hides the Delete role button when `canDelete === false`. Calling
  // `deleteWorkspaceRole` while blocked is a logged no-op (defence-in-depth).
  const defaultMemberRole = workspaceRoles.find(
    ({ defaultRoleType }) => defaultRoleType === DefaultRoleTypesEnum.MEMBER
  );
  const canDelete = !!defaultMemberRole;

  const deleteWorkspaceRole = async (id: string, onSuccess?: () => void) => {
    if (!defaultMemberRole) {
      logger.error('Cannot delete workspace role: default Member role not found in workspace roles event', {
        workspaceId,
        roleId: id,
      });
      return;
    }

    try {
      const currentUserId = matrixClient.getUserId();
      if (!currentUserId) throw new Error('Current user not found');

      const updatedRoles = workspaceRoles.filter((role) => role.id !== id);
      // Assignments whose matrixId is null (pending 3pid invites) are rewritten alongside
      // resolved Matrix-ID assignments — the audit trail (`pendingEmail`, `assignedBy`) is
      // preserved; only `roleId` flips to the default Member role.
      const updatedRoleAssignments = roleAssignments.map((assignment) =>
        assignment.roleId === id
          ? { ...assignment, roleId: defaultMemberRole.id, assignedBy: currentUserId }
          : assignment
      );

      await matrixClient.sendStateEvent(workspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
        version: WORKSPACE_ROLES_EVENT_VERSION,
        roles: updatedRoles,
        assignments: updatedRoleAssignments,
      });

      if (onSuccess) onSuccess();
      pushToast({ variant: 'success', title: t('workspaces.role_removed_successfully'), icon: Check });
    } catch (error) {
      logger.error('Failed to delete workspace role', { error, workspaceId, roleId: id });
      pushToast({ variant: 'info', title: t('something_went_wrong_try_again_later') });
    }
  };

  return { deleteWorkspaceRole, canDelete };
};

export default useDeleteWorkspaceRole;
